import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { Panel } from "primereact/panel";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";

import { useHelpers } from "../../components/2.1/helper/HelperService";

const HELPER_VISIBILITY_STORAGE_KEY = 'helperGlobalVisible';
const HELP_ITEMS_PER_PAGE = 100;

const getItemModuleCode = (item) => {
  const moduleValue = item?.module_code || item?.module?.code || item?.module || 'altalanos';

  return typeof moduleValue === 'string' ? moduleValue : 'altalanos';
};

const getItemModuleName = (item) => {
  return item?.module?.name || getItemModuleCode(item).toUpperCase();
};

export const HelperPage = (props) => {
  const {t} = props;
  const history = useHistory();
  const { helpModule, helpScreen, helpSection } = useParams();

  const helperContext = useHelpers();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    itemPerPage: HELP_ITEMS_PER_PAGE,
    totalItemCount: 0,
  });
  const [page, setPage] = useState(1);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [helperVisible, setHelperVisible] = useState(
    localStorage.getItem(HELPER_VISIBILITY_STORAGE_KEY) !== 'false'
  );

  const hasFilter = Boolean(helpModule || helpScreen || helpSection);

  useEffect(() => {
    setPage(1);
    setExpandedItemId(null);
  }, [helpModule, helpScreen, helpSection]);

  useEffect(() => {
    document.title = t('help') + ' - ' + process.env.REACT_APP_TITLE;

    setTimeout(function () {
      if (document.getElementsByClassName('activeMenu')[0]) {
        document.getElementsByClassName('activeMenu')[0].classList.remove("activeMenu")
      }
      if (document.getElementsByClassName('helper')[0]) {
        document.getElementsByClassName('helper')[0].classList.add("activeMenu")
      }
    }, 1000)
  }, [window.App?.state?.rerenderHelperPage]);

  useEffect(() => {
    const updateHelperVisibility = () => {
      setHelperVisible(localStorage.getItem(HELPER_VISIBILITY_STORAGE_KEY) !== 'false');
    };

    window.addEventListener('storage', updateHelperVisibility);
    window.addEventListener('helperVisibilityChanged', updateHelperVisibility);

    return () => {
      window.removeEventListener('storage', updateHelperVisibility);
      window.removeEventListener('helperVisibilityChanged', updateHelperVisibility);
    };
  }, []);

  useEffect(() => {
    if (!helperContext) {
      return;
    }

    setLoading(true);

    const request = hasFilter
      ? helperContext.loadHelpContentResponse({
        module: helpModule,
        screen: helpScreen,
        section: helpSection,
        page,
        itemPerPage: HELP_ITEMS_PER_PAGE,
      })
      : helperContext.loadAllHelpContents({
        itemPerPage: HELP_ITEMS_PER_PAGE,
      });

    request.then((result) => {
      const nextItems = result?.items || [];

      setItems(nextItems);
      setMeta(result?.meta || {
        page,
        itemPerPage: HELP_ITEMS_PER_PAGE,
        totalItemCount: nextItems.length,
      });

      if (helpScreen || helpSection) {
        setExpandedItemId(nextItems[0]?.id || null);
      }
    }).finally(() => {
      setLoading(false);
    });
  }, [helperContext, helpModule, helpScreen, helpSection, page, hasFilter]);

  const modules = useMemo(() => {
    const moduleMap = (items || []).reduce((acc, item) => {
      const moduleName = getItemModuleCode(item);

      if (!acc[moduleName]) {
        acc[moduleName] = {
          name: moduleName,
          label: getItemModuleName(item),
          count: 0,
        };
      }

      acc[moduleName].count += 1;

      return acc;
    }, {});

    return Object.values(moduleMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const totalPages = Math.max(1, Math.ceil((meta.totalItemCount || 0) / HELP_ITEMS_PER_PAGE));

  const setGlobalHelperVisibility = (checked) => {
    localStorage.setItem(HELPER_VISIBILITY_STORAGE_KEY, checked ? 'true' : 'false');
    setHelperVisible(checked);
    window.dispatchEvent(new Event('helperVisibilityChanged'));
  };

  const toggleItem = (itemId) => {
    setExpandedItemId((currentItemId) => currentItemId === itemId ? null : itemId);
  };

  const renderPager = () => {
    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className="helper-page-pager">
        <Button
          type="button"
          icon="pi pi-angle-left"
          label={t('previous')}
          className="p-button-sm p-button-secondary p-button-text"
          disabled={page <= 1}
          onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
        />
        <span className="helper-page-pager-info">
          {page} / {totalPages}
        </span>
        <Button
          type="button"
          icon="pi pi-angle-right"
          iconPos="right"
          label={t('next')}
          className="p-button-sm p-button-secondary p-button-text"
          disabled={page >= totalPages}
          onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
        />
      </div>
    );
  };

  const renderModuleList = () => {
    if (loading) {
      return (
        <p className={"p-text-center p-my-5"}>
          <i className={"pi pi-spin pi-spinner p-d-block p-mb-3"} style={{ fontSize: '3em' }}></i>
          {t('helpLoadingShort')}
        </p>
      );
    }

    if (modules.length === 0) {
      return (
        <p className={"p-text-center p-my-5"}>
          <i className={"pi pi-info-circle p-d-block p-mb-3"} style={{ fontSize: '3em' }}></i>
          {t('helpNoContent')}
        </p>
      );
    }

    return (
      <>
        <div className="helper-module-grid">
          {modules.map((moduleItem) => (
            <button
              key={moduleItem.name}
              type="button"
              className="helper-module-card"
              onClick={() => history.push('/help/' + moduleItem.name)}
            >
              <span className="helper-module-name">
                {moduleItem.label}
              </span>
              <span className="helper-module-count">
                {/* {moduleItem.count} témakör */}
              </span>
              <i className="pi pi-angle-right"></i>
            </button>
          ))}
        </div>
      </>
    );
  };

  const renderTopicList = () => {
    if (loading) {
      return (
        <p className={"p-text-center p-my-5"}>
          <i className={"pi pi-spin pi-spinner p-d-block p-mb-3"} style={{ fontSize: '3em' }}></i>
          {t('helpLoading')}
        </p>
      );
    }

    if (!items || items.length === 0) {
      return (
        <p className={"p-text-center p-my-5"}>
          <i className={"pi pi-info-circle p-d-block p-mb-3"} style={{ fontSize: '3em' }}></i>
          {t('helpNoContent')}
        </p>
      );
    }

    return (
      <>
        <div className="helper-topic-list">
          {items.map((item) => {
            const isExpanded = expandedItemId === item.id;

            return (
              <div key={item.id} className="helper-topic-item">
                <button
                  type="button"
                  className="helper-topic-title"
                  onClick={() => toggleItem(item.id)}
                >
                  <span>{item.title}</span>
                  <i className={isExpanded ? 'pi pi-angle-up' : 'pi pi-angle-down'}></i>
                </button>

                {isExpanded && (
                  <div
                    className="helper-topic-body"
                    dangerouslySetInnerHTML={{
                      __html: item.body || '',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        {renderPager()}
      </>
    );
  };

  return (
    <div className={"HelperPage"}>
      {localStorage.getItem('userToken') ? (
        <div className={"PageContent p-p-5 p-mx-auto"}>
          <div className={"p-grid"}>
            <div className={"p-col-12 p-lg-6 p-col-align-center p-text-center p-text-lg-left"}>
              <h2 className={"p-m-0 p-p-0 text-primary p-d-inline"}>
                {t('help')}
              </h2>
            </div>

            <div className={"p-col-12 p-lg-6 p-col-align-center p-text-center p-text-lg-right"}>
              {hasFilter && (
                <a
                  onClick={() => {
                    history.push('/help')
                  }}
                  className={"p-button p-button-secondary p-ml-2"}
                >
                  <i className={"pi pi-arrow-left"}></i>
                  &nbsp;
                  {t('backToAllHelp')}
                </a>
              )}
            </div>
          </div>

          <Panel>
            {!hasFilter && (
              <div className="helper-settings-row">
                <div className="helper-settings-control">
                  <span>{t('showHelperEverywhere')}</span>
                  <InputSwitch
                    checked={helperVisible}
                    onChange={(event) => setGlobalHelperVisibility(event.value)}
                  />
                </div>
              </div>
            )}

            {!hasFilter ? renderModuleList() : renderTopicList()}
          </Panel>
        </div>
      ) : (
        <p className={"p-text-center p-my-5 p-py-5"}>
          <i className={"pi pi-exclamation-circle p-d-block p-mb-5"} style={{ fontSize: '5em' }}></i>
          <span className={"p-text-bold p-d-block "}>
            {t('helpLoginRequired')}
          </span>
        </p>
      )}
    </div>
  )
}

export default withTranslation('common')(HelperPage);
